import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { myReferralTimeseries } from "@/lib/api";

/**
 * User's own daily referral growth chart on the Dashboard.
 * Shows the last 30 days: bars for daily signups, area for cumulative total.
 */
export const ReferralChartWidget = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    myReferralTimeseries(30)
      .then((r) => setData(r.series || []))
      .catch(() => setData([]));
  }, []);

  if (data === null) {
    return (
      <section data-testid="referral-chart-widget" className="border border-zinc-800 bg-zinc-950/60 p-5">
        <div className="text-zinc-300 font-mono text-xs">loading…</div>
      </section>
    );
  }

  const total = data.reduce((sum, d) => sum + d.count, 0);
  const peak = Math.max(...data.map((d) => d.count), 0);

  return (
    <section data-testid="referral-chart-widget" className="border border-zinc-800 bg-zinc-950/60 p-5">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e] font-mono">// your referral growth · last 30 days</div>
        <div className="flex items-center gap-4 text-xs text-zinc-200 font-mono">
          <span><span className="text-[#22c55e] font-bold" data-testid="chart-total">{total}</span> total</span>
          <span><span className="text-white font-bold" data-testid="chart-peak">{peak}</span> peak day</span>
        </div>
      </div>
      <div className="h-56 w-full">
        {total === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center border border-dashed border-zinc-800 px-4">
            <div className="font-mono text-xs text-zinc-300 tracking-wider mb-1">// no referrals yet</div>
            <div className="text-white text-sm">Share your link to see your growth curve here.</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
              <defs>
                <linearGradient id="referralGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.65} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cumulativeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a855f7" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="#1e293b" />
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} tickFormatter={(d) => d.slice(5)} tick={{ fontFamily: "monospace" }} />
              <YAxis stroke="#94a3b8" fontSize={10} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#000", border: "1px solid #334155", fontFamily: "monospace", fontSize: 11 }}
                labelStyle={{ color: "#e2e8f0" }}
                formatter={(v, key) => (key === "cumulative" ? [v, "total"] : [v, "signups"])}
              />
              <Area type="monotone" dataKey="cumulative" stroke="#a855f7" strokeWidth={2} fill="url(#cumulativeGradient)" name="cumulative" />
              <Area type="monotone" dataKey="count" stroke="#22c55e" strokeWidth={2} fill="url(#referralGradient)" name="daily" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
};
